/* eslint-disable react/no-array-index-key */
/* eslint-disable react/forbid-prop-types */
import React, { useLayoutEffect, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import ToolTipNoIcon from '../../elements/ToolTipNoIcon';

// generates a row (<tr>) of headers for the table layout definition passed as prop
export default function TableHeaders({
  tableLayout,
  tableState,
  setTableState,
  hiddenColumns,
  setHiddenColumns,
  gridTemplateColumns,
  selectMode,
  selectionColumnProps,
}) {
  const layoutColumns = tableLayout?.columns ?? [];
  const headerCheckboxRef = useRef(null);
  const headerIndeterminate = selectionColumnProps?.indeterminate;
  const headerChecked = Boolean(selectionColumnProps?.checked);
  const headerDisabled = Boolean(selectionColumnProps?.disabled);
  const headerOnChange = selectionColumnProps?.onChange;

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = Boolean(selectMode && headerIndeterminate);
  }, [selectMode, headerIndeterminate]);

  return (
    <div role="rowgroup">
      <div
        className="w-full bg-gray-50 bg-opacity-70 sticky top-0 z-10 border-b border-gray-200 grid gap-3 pl-3 pr-1 lg:pr-3"
        style={gridTemplateColumns ? { gridTemplateColumns } : undefined}
        role="row"
      >
        {selectMode && (
          <div role="columnheader" className="flex items-center py-3">
            <input
              ref={headerCheckboxRef}
              type="checkbox"
              className="h-4 w-4 text-brandBlue-600 border-gray-300 rounded focus:ring-brandBlue-500"
              checked={headerChecked}
              onChange={(event) => {
                event.stopPropagation();
                headerOnChange?.();
              }}
              disabled={headerDisabled}
              aria-checked={headerIndeterminate ? 'mixed' : headerChecked}
              aria-label="Toggle select all rows"
            />
          </div>
        )}
        {/* padding right also needs to offset the scrollbar to match the data div */}
        {layoutColumns.map((columns, index) => (
          // each HeaderCell contains between one and usually three columns, depending on how small the page is
          <HeaderCell
            key={columns?.[0]?.id ?? index}
            columns={columns}
            tableState={tableState}
            setTableState={setTableState}
            setHiddenColumns={setHiddenColumns}
            firstLayoutColumn={index === 0}
            hidden={hiddenColumns.includes(columns?.[0]?.id) ? 'hidden' : ''} // returns a string!
            classNamesHeader={columns?.[0]?.classNamesHeader}
          />
        ))}
      </div>
    </div>
  );
}
TableHeaders.propTypes = {
  tableLayout: PropTypes.shape({
    columnParams: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  }).isRequired,
  tableState: PropTypes.object.isRequired,
  setTableState: PropTypes.func.isRequired,
  hiddenColumns: PropTypes.array.isRequired,
  setHiddenColumns: PropTypes.func.isRequired,
  gridTemplateColumns: PropTypes.string,
  selectMode: PropTypes.bool,
  selectionColumnProps: PropTypes.shape({
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
  }),
};
TableHeaders.defaultProps = {
  gridTemplateColumns: '',
  selectMode: false,
  selectionColumnProps: null,
};

// generates the contents of a header cell (<th>)
function HeaderCell({ columns, tableState, setTableState, firstLayoutColumn, setHiddenColumns, hidden, classNamesHeader }) {
  // as there is no possibility to check if the bottom-level descendant of an element is visible, we do it based on computed width
  // if the computed width is below 5px (it usually is 2px), we assume the column is empty and hide it
  const trRef = useRef();
  const columnsToRender = columns || [];
  const headerCellId = columnsToRender?.[0]?.id;

  // TODO if this has to change on window resize, this should also be triggered by a listener on window resize event
  useLayoutEffect(() => {
    const node = trRef.current;
    if (!node) return;

    const allChildrenHidden = Array.from(node.children).every((el) => getComputedStyle(el).display === 'none');

    setHiddenColumns((prev) => {
      const alreadyHidden = prev.includes(headerCellId);
      if (allChildrenHidden && !alreadyHidden) return [...prev, headerCellId];
      if (!allChildrenHidden && alreadyHidden) return prev.filter((id) => id !== headerCellId);
      return prev;
    });
  }, [headerCellId, setHiddenColumns]);

  return (
    <div
      ref={trRef}
      role="columnheader"
      // TODO border-b
      // eslint-disable-next-line max-len
      className={`sticky top-0 pt-3.5 pb-2 text-sm font-semibold text-gray-900 bg-gray-50 bg-opacity-75 backdrop-blur backdrop-filter ${hidden} ${classNamesHeader} ${
        columnsToRender?.[0]?.textAlign || 'text-left'
      }`}
    >
      {columnsToRender.map((column) => {
        if (column.hideHeaderLabel) return null;
        return <HeaderLabel key={column.id} columnDef={column} tableState={tableState} setTableState={setTableState} firstLayoutColumn={firstLayoutColumn} />;
      })}
    </div>
  );
}
HeaderCell.propTypes = {
  columns: PropTypes.array.isRequired,
  tableState: PropTypes.object.isRequired,
  setTableState: PropTypes.func.isRequired,
  firstLayoutColumn: PropTypes.bool.isRequired,
  setHiddenColumns: PropTypes.func.isRequired,
  hidden: PropTypes.string.isRequired,
  classNamesHeader: PropTypes.string,
};
HeaderCell.defaultProps = {
  classNamesHeader: '',
};

function HeaderLabel({ columnDef, tableState, setTableState, firstLayoutColumn }) {
  // tooltip = 'Click to sort by this column or reverse the sorting order.'
  const { t } = useTranslation('accountDetails');

  const tooltip = t([`columns.${columnDef.usesHeader || columnDef.id}.tooltip`, 'columns.standardTooltip']);
  const label = t([`columns.${columnDef.usesHeader || columnDef.id}.label`, `columns.${columnDef.usesHeader || columnDef.id}`, `${columnDef.usesHeader}`]);

  if (columnDef.usesHeader === null) {
    return <div />;
  }

  return (
    <ToolTipNoIcon className="mt-4" classNameOwn={`${columnDef.classNamesBoth || ''} ${columnDef.classNamesHeader || ''}`} info={tooltip}>
      {/* CAUTION: the useLayoutEffect relies on this div ^^ being hidden / not hidden to calculate column hiding logic */}
      <button
        type="button"
        id={columnDef.id}
        className="group justify-between w-full flex pb-1"
        onClick={() => setTableState({ ...tableState, sortBy: columnDef.id, sortDirectionAsc: !tableState.sortDirectionAsc })}
      >
        <div className="flex align-middle w-full">
          <span className="text-[11px] leading-tight xs:text-xs font-bold text-left uppercase tracking-wide mt-0.5 text-gray-900">{label}</span>
          <span className={`${tableState.sortBy === columnDef.id ? 'visible' : 'invisible'} ml-1 lg:ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible`}>
            {tableState.sortBy === columnDef.id && tableState.sortDirectionAsc ? <ChevronUpIcon className="h-5 w-5" aria-hidden="true" /> : <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />}
          </span>
        </div>
      </button>
    </ToolTipNoIcon>
  );
}
HeaderLabel.propTypes = {
  tableState: PropTypes.objectOf(PropTypes.any).isRequired,
  setTableState: PropTypes.func.isRequired,
  columnDef: PropTypes.object.isRequired,
  firstLayoutColumn: PropTypes.bool.isRequired,
};
HeaderLabel.defaultProps = {};
